import { useEffect, useRef } from "react";

type UseRunOnChangeCallback<T> = (prev: T, next: T) => void;

export function useRunOnChange<T>(
  value: T,
  callback: UseRunOnChangeCallback<T>,
  opts?: { noRunOnMount?: boolean }
) {
  const ref = useRef<
    { hasMounted: false } | { hasMounted: true; lastValue: T }
  >({
    hasMounted: false
  });

  useEffect(() => {
    const isMount = !ref.current.hasMounted;
    if (isMount) {
      ref.current = {
        hasMounted: true,
        lastValue: value
      };
    }
    if (!ref.current.hasMounted) {
      // Has mounted should have been set in all code paths.
      throw new Error("Should never happen");
    }
    const hasChanged = ref.current.lastValue !== value;
    const runningCallbackOnMount = isMount && !opts?.noRunOnMount;

    const shouldRun = hasChanged || runningCallbackOnMount;

    if (!shouldRun) return;

    if (callback.length === 1) {
      const singleArgumentCallback = callback as (x: T) => void;
      singleArgumentCallback(value);
    } else {
      callback(ref.current.lastValue, value);
    }
    ref.current.lastValue = value;
  }, [value, callback, opts]);
}
